import { Check } from "lucide-react";

export interface TrackerStep<K extends string = string> {
  key: K;
  label: string;
}

interface StepTrackerProps<K extends string> {
  steps: TrackerStep<K>[];
  active: K;
  onSelect: (key: K) => void;
  /** Steps with real work behind them — these tick green. Everything else stays grey. */
  done?: K[];
  /**
   * Steps that cannot have produced anything yet — dimmed, the way a disabled primary action is.
   *
   * The inverse of `done`: not "this produced something" but "this cannot have yet". Findings before
   * a compliance run is the case it exists for.
   *
   * NOT A GATE. A pending step is still selectable, and deliberately so — see the note below. It is
   * a signal about the state of the work, not a lock on the reader.
   */
  pending?: K[];
  /**
   * Nothing selectable.
   *
   * Only for a walkthrough driving itself — a tracker that is REPORTING rather than offering. Never
   * use it to gate somebody out of a step they should be able to reach.
   */
  locked?: boolean;
}

/**
 * Connected numbered circles across the top of a workspace, any of them clickable.
 *
 * The same tracker serves a project's stages and a standard's — they are the same idea, a piece of
 * work moving through ordered steps, and having them look different taught a reader nothing.
 *
 * NEVER gated: every circle is selectable whatever state anything is in. The green tick says "this
 * step produced something", never "you may not pass" — and a dimmed circle says "this cannot have
 * produced anything yet", which is also not a refusal. Somebody who wants to see what a stage IS
 * should be able to look at it and find an honest empty state.
 */
function StepTracker<K extends string>({
  steps,
  active,
  onSelect,
  done = [],
  pending = [],
  locked = false,
}: StepTrackerProps<K>) {
  return (
    <div className={`vr-steps${locked ? " vr-steps-locked" : ""}`} role="tablist">
      {steps.map((step, idx) => {
        const isActive = step.key === active;
        const isDone = done.includes(step.key);
        // Done wins: a step that produced something is never also pending.
        const isPending = !isDone && pending.includes(step.key);
        const isLast = idx === steps.length - 1;
        const cls = [
          "vr-step",
          isActive ? "vr-step-active" : "",
          isDone ? "vr-step-done" : "",
          isPending ? "vr-step-pending" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div key={step.key} className={cls}>
            {/* The connector sits behind the circles, and only fills once this step is done. */}
            {!isLast && <span className={`vr-step-line${isDone ? " vr-on" : ""}`} />}
            <button
              type="button"
              role="tab"
              aria-selected={isActive}
              className="vr-step-circle"
              onClick={() => !locked && onSelect(step.key)}
              disabled={locked}
              title={step.label}
            >
              {isDone ? <Check size={13} /> : idx + 1}
            </button>
            <span className="vr-step-label">{step.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default StepTracker;
